import { useQuery } from 'react-query';
import { useCmpIntContext, useIsDemo, useCompIntDeviceView } from 'hooks';
import { DEFAULT_STALE_TIME } from '../../constants';
import { useState, useRef, useEffect, useMemo } from 'react';

// API REQUESTS
import { fetchCompOverviewData } from 'api';

import { OVERVIEW_TABS, COMP_INTEL_AD_TYPES } from '../../constants/comp-intel';

// COMPONENTS
import OutletSpinner from '../OutletSpinner';
import SectionMessage from '../SectionMessage';
import TooltipPopover from 'components/TooltipPopover';
import OverviewChart from './OVComponents/OverviewChart';
import OverviewLineChart from './OVComponents/OverviewLineChart';
import DeviceSupportTooltip from 'components/DeviceSupportToolTip';

// TYPES
import { OverviewTab, CompIntelAdType, OverviewDailyData } from '../../types';

// UTILS
import {
  computeDailyCoverageForEachDevice,
  downloadChartData,
  filterOverviewData,
  downloadCoverageCompData,
} from 'utils';
import DownloadMenu from 'components/DownloadMenu';

// COMP. INTEL OVERVIEW COMPONENT
function CompIntelOverview() {
  const {
    dateRange,
    competitorOptions,
    selectedCompetitors,
    selectedSearchOptions,
    setCompetitorOptions,
    setSelectedCompetitors,
    isMobileSupported,
    isDesktopSupported,
  } = useCmpIntContext();
  const { isDemo, topCompetitors } = useIsDemo();

  const dropdownRef = useRef<HTMLDivElement>(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [view, setView] = useCompIntDeviceView();
  const [activeTab, setActiveTab] = useState<OverviewTab>('Coverage');
  const [selectedFilters, setSelectedFilters] = useState<CompIntelAdType[]>([
    'PAID',
  ]);

  // FETCH COMPETITOR CHART (BAR) DATA
  const { data: compOverviewData, isLoading: isLoadingOverviewData } = useQuery(
    [
      'compCompetitorsData',
      dateRange?.startDate,
      dateRange?.endDate,
      selectedSearchOptions.terms,
    ],
    () => fetchCompOverviewData(dateRange, selectedSearchOptions.terms, false),
    {
      enabled: !!dateRange && (isDemo || !!competitorOptions.length),
      staleTime: DEFAULT_STALE_TIME,
    }
  );

  // FETCH DAILY CHART (LINE) DATA
  const {
    data: compDailyData,
    isLoading: isLoadingDailyData,
    // error: compDataDailyError,
  } = useQuery(
    [
      'compDailyData',
      dateRange?.startDate,
      dateRange?.endDate,
      selectedSearchOptions.terms,
    ],
    () => fetchCompOverviewData(dateRange, selectedSearchOptions.terms, true),
    {
      enabled: !!dateRange && (isDemo || !!competitorOptions.length),
      staleTime: DEFAULT_STALE_TIME,
    }
  );

  useEffect(() => {
    if (!isDemo) return;
    setSelectedCompetitors(topCompetitors.slice(0, 15));
  }, [isDemo, topCompetitors, setSelectedCompetitors]);

  // SET UNIQUE COMPETITOR (DOMAIN) OPTIONS BASED ON THOSE FOUND IN COMPETITOR GROUPS & DATA
  useEffect(() => {
    setCompetitorOptions((prev) => {
      return Array.from(
        new Set([
          ...prev,
          ...(compOverviewData?.unique_brands || []),
          ...(compDailyData?.unique_brands || []),
        ])
      );
    });
  }, [compOverviewData, compDailyData, setCompetitorOptions]);

  // FILTERED DAILY CHART DATA
  const filteredDailyData = useMemo(
    () => computeDailyCoverageForEachDevice(compDailyData, selectedCompetitors),
    [compDailyData, selectedCompetitors]
  );

  // FILTERED COMPETITOR CHART DATA
  const filteredOverviewData = useMemo(
    () => filterOverviewData(compOverviewData, selectedCompetitors),
    [compOverviewData, selectedCompetitors]
  );

  // HANDLE FILTER TOGGLE (updated to support single selection)
  const toggleFilter = (filter: CompIntelAdType) => {
    setSelectedFilters([filter]); // Replace with the newly selected filter
  };

  // DROWDOWN (IS THIS STILL NEEDED?)
  const toggleDropdown = () => {
    setIsDropdownOpen((prev) => !prev);
  };
  const closeDropdown = () => {
    setIsDropdownOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        closeDropdown();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const downloadXLSData = () => {
    downloadChartData({
      activeTab,
      filters: selectedFilters,
      overviewData: filteredOverviewData,
      device: view.toLowerCase() as 'desktop' | 'mobile',
    });
    downloadCoverageCompData({
      activeTab,
      filters: selectedFilters,
      dailyData: filteredDailyData,
      device: view.toLowerCase() as 'desktop' | 'mobile',
    });
  };

  // JSX
  return (
    <div
      id="CompetitiveIntelligenceOverview"
      className="mb-4 bg-light border rounded-2xl py-2"
    >
      <div className="flex justify-between items-center mb-2 px-4">
        <div>
          <h2 className="text-2xl font-bold">Overview</h2>
          <p className="text-gray800 text-sm font-bold mt-[2px]">
            How the competition and market is evolving
          </p>
        </div>
        <div
          className="flex items-center"
          ref={dropdownRef}
          id="downloadDropdownMenu-CompetitiveIntelligenceOverview"
        >
          {isMobileSupported && isDesktopSupported ? (
            <div className="flex items-center mr-4 text-xs">
              <span
                className={
                  view === 'desktop'
                    ? 'text-primarygray mr-2'
                    : 'text-gray400 mr-2'
                }
              >
                Desktop
              </span>
              <label className="inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  value=""
                  className="sr-only peer"
                  checked={view === 'mobile'}
                  onChange={() =>
                    setView((prev) =>
                      prev === 'desktop' ? 'mobile' : 'desktop'
                    )
                  }
                />
                <div className="relative w-10 h-5 bg-lightblue rounded-full peer-checked:after:translate-x-full peer-checked:after:border-light after:content-[''] after:absolute after:top-0.5 after:start-[5px] after:bg-light after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-success"></div>
              </label>
              <span
                className={
                  view === 'mobile'
                    ? 'text-primarygray ml-2'
                    : 'text-gray400 ml-2'
                }
              >
                Mobile
              </span>
            </div>
          ) : null}
          <DeviceSupportTooltip
            isMobileSupported={isMobileSupported}
            isDesktopSupported={isDesktopSupported}
            view={view}
          />
          <DownloadMenu
            id="downloadDropdownMenu-CompetitiveIntelligenceOverview"
            onDownloadXLS={() => downloadXLSData()}
            imageParam="CompetitiveIntelligenceOverview"
          />
        </div>
      </div>
      <hr className="border-success mb-6" />
      <div className="flex flex-col lg:flex-row mb-4">
        <div className="flex justify-between items-center p-2 w-full px-4 whitespace-nowrap flex-wrap gap-4">
          <div className="flex space-x-2 bg-fourthgray py-1 px-1 rounded-lg">
            {OVERVIEW_TABS.map((tab) => (
              <TooltipPopover
                key={tab}
                content={`competitive_intelligence_page.overview_tab.${tab
                  .replace('# ', '')
                  .replace(' ', '_')
                  .toLowerCase()}`}
                type="popup"
                side="bottom"
                tooltipWidth="max-w-64"
              >
                <div key={tab} className="relative group inline-block">
                  <button
                    className={`${
                      activeTab === tab
                        ? 'px-4 py-[9px] rounded bg-light text-primarygray'
                        : 'text-thirdgray px-4 py-[9px]'
                    }`}
                    onClick={() => setActiveTab(tab)}
                  >
                    {tab}
                  </button>
                </div>
              </TooltipPopover>
            ))}
          </div>
          <div className="flex space-x-1">
            {COMP_INTEL_AD_TYPES.map((filter) => (
              <TooltipPopover
                key={filter}
                content={`competitive_intelligence_page.overview_tab.${filter.toLowerCase()}`}
                type="popup"
                side="top"
              >
                <span
                  key={filter}
                  className={`text-thirdgray rounded px-3 py-1 cursor-pointer ${
                    selectedFilters.includes(filter)
                      ? 'border border-success rounded'
                      : 'border border-disgreen100 rounded'
                  }`}
                  onClick={() => toggleFilter(filter)} // Call the updated toggleFilter
                >
                  {filter}
                  {selectedFilters.includes(filter) && (
                    <span className="ml-1">✕</span>
                  )}
                </span>
              </TooltipPopover>
            ))}
          </div>
        </div>
      </div>
      {isLoadingOverviewData ? (
        <div className="py-10 mb-8">
          <OutletSpinner />
        </div>
      ) : filteredOverviewData ? (
        <OverviewChart
          data={filteredOverviewData}
          device={view.toLowerCase()}
          activeTab={activeTab}
          selectedFilters={selectedFilters}
          competitorsToBeIncluded={selectedCompetitors}
        />
      ) : (
        <SectionMessage variant="empty" />
      )}

      {isLoadingDailyData ? (
        <div className="py-10 mb-8">
          <OutletSpinner />
        </div>
      ) : filteredDailyData ? (
        <OverviewLineChart
          data={filteredDailyData}
          device={view.toLowerCase()}
          activeTab={activeTab}
          selectedFilters={selectedFilters}
          competitorsToBeIncluded={selectedCompetitors}
        />
      ) : (
        <SectionMessage variant="empty" />
      )}
    </div>
  );
}

export default CompIntelOverview;
